import axios from 'axios'
import { ActionTree, GetterTree, MutationTree } from 'vuex'
import VuexPersistence from 'vuex-persist'
import { ADD_CART_ITEMS, CREATE_CART, DELETE_BOOKINGS, DELETE_CART, DELETE_CART_ITEM, LOAD_CART, SET_CART, SET_CART_ACTIVITIES, SET_CART_VACANCIES, SET_PREVIOUS_PRODUCT_URL, UPDATE_CART, UPDATE_CART_ITEM, UPDATE_CART_ITEMS } from '../types'
import { IFacility } from './facility'
import { IVacancies } from './timeslot'
import { groupBy, map, uniq, result } from 'lodash-es'
import { RootState } from '..'
import { omit } from 'lodash-es'
import { transformMetadata } from '@/utils/cartMetadata'

export interface IMetadata {
  name: string
  value: string
}

export interface IItem {
  _id?: string
  metadata: IMetadata[]
  price: number
  productId: string
  quantity: number
  variantId: string
}

export interface ICartItem {
  _id: string
  item: IItem
  activity?: IFacility
  vacancy?: IVacancies
}

export interface IMetadataTransform {
  productName?: string
  productImage?: string
  productUrl?: string
  variantName?: string
  sku?: string
  dateOfVisit?: string
  start?: string
  end?: string
  tags?: string
  timeslotName?: string
  acknowledgement?: string
  groupMin?: string
  cmsgTickets?: string
  holdBookingId?: string
  bookingParams?: string
}

export interface ICart {
  _id: string
  catalogue: string
  items: IItem[]
  cartItems: ICartItem[]
  merchant: string
  user: string
  expiredAt: string
  createdAt: string
  updatedAt: string
}
export interface ICreateCartPayload {
  catalogue?: string
  items: Omit<IItem, 'price'>[]
}

export interface IUpdateCartPayload {
  catalogue?: string
  items: Omit<IItem, 'price'>[]
}

export interface IQuoteCartPayload {
  items: Omit<IItem, 'price'>[]
}

export interface ICartQuoteDiscount {
  amount: number
  automaticDiscount: boolean
  label: string
}

export interface ICartQuoteLine {
  discountedTotalAmount: number
  discountAmount: number
  originalTotalAmount: number
  productId: string
  quantity: number
  variantId: string
}

export interface ICartQuoteResponse {
  discounts: ICartQuoteDiscount[]
  lines: ICartQuoteLine[]
  subtotalAmount: number
  taxAmount: number
  taxesIncluded: boolean
  totalAmount: number
  totalDiscountAmount: number
}

export interface ICartQuantity {
  _id: string
  totalPax: number
  maxPax: number
}

export interface ICartProductQuantity {
  productId: string
  totalPax: number
  maxPax: number
  isLimited: boolean
}

export interface CartState {
  cart?: ICart
  previousProductUrl?: string
  activities: Record<string, IFacility[]>
  vacancies: Record<string, Record<string, IVacancies[]>>
}

const state: CartState = {
  cart: undefined,
  previousProductUrl: undefined,
  activities: {},
  vacancies: {},
}

export function getCartItemSlotQuantityKey(cartItem: ICartItem): string {
  const { dateOfVisit = '', start = '', end = '' } = transformMetadata(cartItem.item.metadata)
  return `${cartItem.item.productId}|${dateOfVisit}|${start}|${end}`
}

const getters: GetterTree<CartState, RootState> = {
  totalItems: (state) => state.cart?.cartItems.reduce((sum, cartItem) => sum + cartItem.item.quantity, 0),
  cart: (state) => state.cart,
  previousProductUrl: (state) => state.previousProductUrl,
  cartQuantities: (state): ICartQuantity[] => {
    if (state.cart) {
      const cartItemGroups = groupBy(state.cart.cartItems, getCartItemSlotQuantityKey)
      return map(cartItemGroups, (cartItems, _id) => {
        const { vacancy } = cartItems[0]
        return {
          _id,
          totalPax: cartItems.reduce((sum, cartItem) => {
            return sum + (cartItem.item.quantity ?? 0)
          }, 0),
          maxPax: vacancy?.maxPax ?? 0,
        }
      })
    } else {
      return []
    }
  },
  cartProductQuantities: (state): ICartProductQuantity[] => {
    if (state.cart) {
      const cartItemGroups = groupBy(state.cart.cartItems, (cartItem) => {
        return cartItem.item.productId
      })
      return map(cartItemGroups, (cartItems, productId) => {
        const { activity } = cartItems[0]
        const maxPaxPerBooking = Number(result<number>(activity, 'maxPaxPerBooking', 0) ?? 0)
        return {
          productId,
          totalPax: cartItems.reduce((sum, cartItem) => {
            return sum + (cartItem.item.quantity ?? 0)
          }, 0),
          maxPax: maxPaxPerBooking,
          isLimited: maxPaxPerBooking > 0,
        }
      })
    } else {
      return []
    }
  },
  cartSubtotal: (state, getters) => {
    if (state.cart) {
      return state.cart.cartItems
        .filter(cartItem => {
          const cartQuantity: ICartQuantity | undefined = getters.cartQuantities.find(
            (quantity: ICartQuantity) => quantity._id === getCartItemSlotQuantityKey(cartItem))
          const cartProductQuantity: ICartProductQuantity | undefined = getters.cartProductQuantities.find(
            (quantity: ICartProductQuantity) => quantity.productId === cartItem.item.productId)
          const slotAvailable = cartQuantity ? cartQuantity.totalPax <= cartQuantity.maxPax : true
          const productAvailable = cartProductQuantity
            ? !cartProductQuantity.isLimited || cartProductQuantity.totalPax <= cartProductQuantity.maxPax
            : true
          return !!cartItem.vacancy && slotAvailable && productAvailable
        })
        .reduce((sum, cartItem) => {
          return sum + (cartItem.item.price * cartItem.item.quantity)
        }, 0)
    } else {
      return 0
    }
  },
  cartActivities: (state) => state.activities,
  cartVacancies: (state) => state.vacancies,
}

const mutations: MutationTree<CartState> = {
  [SET_CART](state, cart?: ICart) {
    state.cart = cart
  },
  [SET_CART_ACTIVITIES](state, activities: Record<string, IFacility[]>) {
    state.activities = activities
  },
  [SET_CART_VACANCIES](state, vacancies: Record<string, Record<string, IVacancies[]>>) {
    state.vacancies = vacancies
  },
  [SET_PREVIOUS_PRODUCT_URL](state, url?: string) {
    state.previousProductUrl = url
  },
}

const actions: ActionTree<CartState, RootState> = {
  [LOAD_CART]: async ({ commit }, cartId: string) => {
    try {
      const { getCart } = await import('@/utils/cart')
      const cart = await getCart(cartId)
      commit(SET_CART, cart)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        commit(SET_CART, undefined)
      } else {
        throw error
      }
    }
  },
  [CREATE_CART]: async (
    { dispatch },
    payload: ICreateCartPayload
  ) => {
    const { createCart } = await import('@/utils/cart')
    const cart = await createCart(payload)
    await dispatch(LOAD_CART, cart._id)
  },
  [UPDATE_CART]: async (
    { state, dispatch },
    payload: IUpdateCartPayload,
  ) => {
    if (state.cart) {
      const { updateCart } = await import('@/utils/cart')
      await updateCart(state.cart._id, payload)
      await dispatch(LOAD_CART, state.cart._id)
    }
  },
  [DELETE_CART]: async (
    { state, commit },
  ) => {
    if (state.cart) {
      try {
        const { deleteCart } = await import('@/utils/cart')
        await deleteCart(state.cart._id)
        commit(SET_CART, undefined)
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          commit(SET_CART, undefined)
        } else {
          throw error
        }
      }
    }
  },
  [ADD_CART_ITEMS]: async (
    { state, dispatch, rootGetters },
    payload: Omit<IItem, 'price'>[],
  ) => {
    if (state.cart) {
      const updatedItems = state.cart.cartItems.map(cartItem => omit(cartItem.item, 'price'))

      payload.forEach(newItem => {
        const existingItemIndex = updatedItems.findIndex(
          item => {
            const metadata = transformMetadata(item.metadata)
            const newMetadata = transformMetadata(newItem.metadata)
            return item.productId === newItem.productId &&
              item.variantId === newItem.variantId &&
              metadata.dateOfVisit === newMetadata.dateOfVisit &&
              metadata.start === newMetadata.start &&
              metadata.end === newMetadata.end
          }
        )

        if (existingItemIndex >= 0) {
          // Update quantity if item exists
          updatedItems[existingItemIndex].quantity += newItem.quantity
        } else {
          // Add new item if it doesn't exist
          updatedItems.push(newItem)
        }
      })

      const { isCartExpired } = await import('@/utils/cart')
      if (!isCartExpired(state.cart)) {
        await dispatch(UPDATE_CART, { items: updatedItems })
      } else {
        await dispatch(CREATE_CART, { items: updatedItems })
      }
    } else {
      await dispatch(CREATE_CART, { items: payload })
    }

    const { dotdigitalCartUpdate } = await import('@/utils/dotdigital')
    dotdigitalCartUpdate(state.cart, rootGetters.currency)
  },
  [UPDATE_CART_ITEM]: async (
    { state, dispatch, rootGetters },
    payload: Omit<IItem, 'price'>,
  ) => {
    if (state.cart) {
      const updatedItems = state.cart.cartItems.map(cartItem => omit(cartItem.item, 'price'))

      await dispatch(UPDATE_CART, {
        items: updatedItems.map(item => {
          return item._id === payload._id ? payload : item
        })
      })
      const { dotdigitalCartUpdate } = await import('@/utils/dotdigital')
      dotdigitalCartUpdate(state.cart, rootGetters.currency)
    }
  },
  [UPDATE_CART_ITEMS]: async (
    { state, dispatch },
    payload: Omit<IItem, 'price'>[],
  ) => {
    if (state.cart) {
      await dispatch(UPDATE_CART, {
        items: state.cart.cartItems.map(cartItem => {
          const item = omit(cartItem.item, 'price')
          const newItem = payload.find(p => {
            return p._id === item._id || p._id === cartItem._id
          })
          return newItem ?? item
        })
      })
    }
  },
  [DELETE_CART_ITEM]: async (
    { state, dispatch, rootGetters },
    cartItemId: string,
  ) => {
    if (state.cart) {
      const updatedItems = state.cart.cartItems.map(cartItem => omit(cartItem.item, 'price'))
      await dispatch(UPDATE_CART, {
        items: updatedItems.filter(item => {
          return item._id !== cartItemId
        })
      })
      const { dotdigitalCartUpdate } = await import('@/utils/dotdigital')
      dotdigitalCartUpdate(state.cart, rootGetters.currency)
    }
  },
  [DELETE_BOOKINGS]: async (
    { state, dispatch },
  ) => {
    if (state.cart) {
      const bookingIds = uniq(
        state.cart.cartItems.map(cartItem => {
          const { holdBookingId = '' } = transformMetadata(
            cartItem.item.metadata
          )
          return holdBookingId
        })
      )
      try {
        const { deleteBooking } = await import('@/utils/facilitiesBooking')
        await Promise.all(bookingIds.map(async bookingId => {
          await deleteBooking(bookingId)
        }))
      } catch (error) {
        console.log(error)
      }
      const updatedItems = state.cart.cartItems.map(cartItem => {
        return {
          ...omit(cartItem.item, 'price'),
          metadata: cartItem.item.metadata.filter(metadata => {
            return metadata.name !== 'holdBookingId' && metadata.name !== 'bookingParams'
          })
        }
      })
      await dispatch(UPDATE_CART, {
        items: updatedItems
      })

    }
  },
}

const VuexLocal = new VuexPersistence({
  storage: window.localStorage,
}) 

export default {
  state,
  getters,
  actions,
  mutations,
  plugins: [VuexLocal.plugin],
}
